import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateCompanyFromSiret } from '@/modules/core/companies/hooks/useCompanies';
import type { CompanyType } from '@/modules/core/companies/types/company.types';
import { COMPANY_TYPE_CONFIG } from '@/modules/core/companies/types/company.types';

interface CompanyAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SIRET_REGEX = /^\d{14}$/;

export function CompanyAddDialog({ open, onOpenChange }: CompanyAddDialogProps) {
  const [siret, setSiret] = useState('');
  const [companyType, setCompanyType] = useState<CompanyType>('prospect');
  const createFromSiret = useCreateCompanyFromSiret();
  const navigate = useNavigate();

  const normalized = siret.replace(/\s/g, '');
  const isValid = SIRET_REGEX.test(normalized);

  const handleSubmit = () => {
    if (!isValid) {
      toast.error('Le SIRET doit contenir 14 chiffres');
      return;
    }

    createFromSiret.mutate(
      { siret: normalized, companyType },
      {
        onSuccess: (company) => {
          toast.success(`Société "${company.name}" ajoutée`);
          setSiret('');
          setCompanyType('prospect');
          onOpenChange(false);
          navigate(`/tm/companies/${company.id}`);
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Erreur lors de la création de la société');
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une société</DialogTitle>
          <DialogDescription>
            Saisir un numéro SIRET. Les informations (nom, adresse) seront
            récupérées automatiquement via l'API INSEE.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="siret">Numéro SIRET</Label>
            <Input
              id="siret"
              placeholder="Ex: 12345678901234"
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              maxLength={17}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              14 chiffres (les espaces sont ignorés)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyType">Type</Label>
            <Select
              value={companyType}
              onValueChange={(value) => setCompanyType(value as CompanyType)}
            >
              <SelectTrigger id="companyType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(COMPANY_TYPE_CONFIG) as CompanyType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {COMPANY_TYPE_CONFIG[type].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || createFromSiret.isPending}
          >
            {createFromSiret.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
